import { useToast } from "@design";
import { useStore } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { errorMessage, schemas, updateSpaceMutation } from "@/api";
import { SaveBar } from "@/components/build/SaveBar";
import { useAppForm } from "@/components/form/form";
import { Page } from "@/components/Page";
import { Text } from "@/components/Text";
import { NO_BRAND, useDefaultBrandOptions } from "@/features/build/brands/useDefaultBrandOptions";
import { SPACE_ICON_NAMES } from "@/features/shell/spaceIcons";
import { ArchivedBanner, DangerZone } from "./SpaceLifecycle";
import { colorOptions } from "./spaceAppearance";
import { useRefreshSpace, useSettingsSpace } from "./useSpaceSettings";

const update = schemas.zSpaceUpdate.shape;

/** Name, description, icon, color and default brand (editor+), plus archive/delete (admin). */
export function GeneralSection() {
  const space = useSettingsSpace();
  const defaultBrand = space.defaultBrand ?? null;
  const { t } = useTranslation("spaces", { keyPrefix: "settings" });
  const brandOptions = useDefaultBrandOptions(defaultBrand);
  const toast = useToast();
  const refresh = useRefreshSpace(space.uuid);
  const save = useMutation(updateSpaceMutation());
  const schema = z.object({
    name: z.string().trim().min(1, t("general.nameRequired")).pipe(update.name.unwrap().unwrap()),
    description: z.string().max(5000),
    icon: z.string(),
    color: z.string(),
    defaultBrandUuid: z.string(),
  });
  const initial = {
    name: space.name,
    description: space.description ?? "",
    icon: space.icon ?? "box",
    color: space.color ?? "#7C16F5",
    defaultBrandUuid: defaultBrand?.uuid ?? NO_BRAND,
  };

  const form = useAppForm({
    defaultValues: initial,
    validators: { onChange: schema, onSubmit: schema },
    onSubmit: async ({ value, formApi }) => {
      try {
        await save.mutateAsync({
          path: { space_uuid: space.uuid },
          body: {
            ...value,
            name: value.name.trim(),
            description: value.description.trim() || null,
            defaultBrandUuid: value.defaultBrandUuid || null,
          },
        });
        await refresh();
        formApi.reset(value);
        toast.success(t("general.saved"));
      } catch (error) {
        toast.error(errorMessage(error, t("general.saveFailed")));
      }
    },
  });
  const dirty = useStore(form.store, (s) => s.isDirty);
  const submitting = useStore(form.store, (s) => s.isSubmitting);

  return (
    <Page title={t("general.label")}>
      {space.status === "archived" && <ArchivedBanner space={space} />}
      <form
        aria-label={t("general.label")}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.AppField name="name">{(f) => <f.TextField label={t("general.name")} required />}</form.AppField>
        <form.AppField name="description">
          {(f) => (
            <f.TextareaField label={t("general.description")} placeholder={t("general.descriptionPlaceholder")} />
          )}
        </form.AppField>
        {!space.isPersonal && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <form.AppField name="icon">
              {(f) => (
                <f.SelectField
                  label={t("general.icon")}
                  options={SPACE_ICON_NAMES.map((n) => ({ value: n, label: n }))}
                />
              )}
            </form.AppField>
            <form.AppField name="color">
              {(f) => (
                <f.SelectField
                  label={t("general.color")}
                  options={colorOptions(initial.color, (k) => t(`colors.${k}`))}
                />
              )}
            </form.AppField>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <form.AppField name="defaultBrandUuid">
            {(f) => <f.SelectField label={t("general.defaultBrand")} options={brandOptions} />}
          </form.AppField>
          <Text variant="caption" tone="tertiary">
            {t("general.defaultBrandHint")}
          </Text>
        </div>
        <SaveBar
          dirty={dirty}
          saving={submitting}
          onSave={() => void form.handleSubmit()}
          onCancel={() => form.reset()}
        />
      </form>
      {space.canManage && !space.isPersonal && <DangerZone space={space} />}
    </Page>
  );
}
